import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Input } from './components/ui/input';
import { Users, RefreshCw, Download, Wand2, AlertTriangle, Check, Filter, ChevronDown, ChevronUp, Menu, X, Upload, FileSpreadsheet, GripVertical, Lock, BarChart3, Archive } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { DndContext, closestCenter, DragOverlay, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import PasscodeScreen, { isAuthenticated, lockSession, useInactivityLock } from './components/PasscodeScreen';
import { SnapshotBar, GroupAvailabilityBadge, GroupSessionBreakdown } from './components/AvailabilityDashboard';
import ReportPage from './ReportPage';
import SeasonManager from './SeasonManager';
import OpenFlexDays from './pages/OpenFlexDays';
import CampfrontAdmin from './pages/admin/CampfrontAdmin';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Draggable Camper Component
function DraggableCamper({ camper, children }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: camper.id,
    data: { camper }
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: isDragging ? 1000 : 'auto',
    opacity: isDragging ? 0.5 : 1,
  } : undefined;

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div className="flex items-center gap-1">
        <button {...listeners} className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded touch-none">
          <GripVertical className="w-3 h-3 text-gray-400" />
        </button>
        {children}
      </div>
    </div>
  );
}

// Droppable Group Component
function DroppableGroup({ groupCode, children, isOver }) {
  const { setNodeRef } = useDroppable({
    id: groupCode,
  });

  return (
    <div 
      ref={setNodeRef} 
      className={`transition-colors ${isOver ? 'bg-blue-50 ring-2 ring-blue-400' : ''}`}
    >
      {children}
    </div>
  );
}

function App() {
  const [authenticated, setAuthenticated] = useState(isAuthenticated());
  const [stats, setStats] = useState(null);
  const [campers, setCampers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [showConflicts, setShowConflicts] = useState(false);
  const [rosterErrors, setRosterErrors] = useState(null);
  const [showRosterErrors, setShowRosterErrors] = useState(false);
  const [comparisonRunning, setComparisonRunning] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState('all');
  const [selectedGender, setSelectedGender] = useState('all');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingSheet, setSyncingSheet] = useState(false);
  const [autoGrouping, setAutoGrouping] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [dragConflictPrompt, setDragConflictPrompt] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const filteredResultsRef = useRef(null);
  const [activeDragCamper, setActiveDragCamper] = useState(null);
  const [activeDropGroup, setActiveDropGroup] = useState(null);
  const [availabilityReport, setAvailabilityReport] = useState(null);
  const [selectedDay, setSelectedDay] = useState(1);
  const [showReport, setShowReport] = useState(false); // kept for compat
  const [showSeasonManager, setShowSeasonManager] = useState(false);
  const [camperHistory, setCamperHistory] = useState({});
  
  const openReportWindow = () => {
    const reportUrl = window.location.origin + '?view=report';
    // On mobile/tablet, navigate directly (popups get blocked); on desktop, open new window
    const isMobile = window.innerWidth < 1024;
    if (isMobile) {
      window.open(reportUrl, '_blank');
    } else {
      window.open(reportUrl, 'rrdc-report', 'width=1600,height=900,scrollbars=yes,resizable=yes');
    }
  };

  // Inactivity lock
  useInactivityLock(
    useCallback(() => {
      if (authenticated) toast.warning('Session will lock in 5 minutes due to inactivity');
    }, [authenticated]),
    useCallback(() => {
      if (authenticated) {
        lockSession();
        setAuthenticated(false);
        setStats(null);
        setCampers([]);
        setGroups([]);
        setConflicts([]);
      }
    }, [authenticated])
  );

  const handleLock = () => {
    lockSession();
    setAuthenticated(false);
    setStats(null);
    setCampers([]);
    setGroups([]);
    setConflicts([]);
  };

  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    })
  );

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, campersRes, groupsRes, conflictsRes, availRes, historyRes] = await Promise.all([
        fetch(`${API_URL}/api/stats`),
        fetch(`${API_URL}/api/campers`),
        fetch(`${API_URL}/api/groups`),
        fetch(`${API_URL}/api/conflicts`),
        fetch(`${API_URL}/api/availability/report`),
        fetch(`${API_URL}/api/campers/history/bulk`)
      ]);
      
      if (statsRes.ok) setStats(await statsRes.json());
      if (campersRes.ok) setCampers(await campersRes.json());
      if (groupsRes.ok) setGroups(await groupsRes.json());
      if (conflictsRes.ok) setConflicts((await conflictsRes.json()).conflicts || []);
      if (availRes.ok) setAvailabilityReport(await availRes.json());
      
      // Fetch roster comparison results
      try {
        const rosterRes = await fetch(`${API_URL}/api/roster-comparison/latest`);
        if (rosterRes.ok) {
          const rosterData = await rosterRes.json();
          if (rosterData.status === 'error' && rosterData.total_mismatches > 0) {
            setRosterErrors(rosterData);
          } else {
            setRosterErrors(null);
          }
        }
      } catch (e) { /* ignore */ }
      
      if (historyRes.ok) {
        const hData = await historyRes.json();
        setCamperHistory(hData.by_camper_id || {});
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, []);

  useEffect(() => {
    if (authenticated) fetchData();
  }, [fetchData, authenticated]);

  const handleSync = async () => {
    setSyncing(true);
    setMobileMenuOpen(false);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 180000); // 3 minute timeout for CampMinder
      
      const res = await fetch(`${API_URL}/api/sync/campminder`, { 
        method: 'POST',
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      // Handle non-JSON responses (like 500 errors)
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(text || `Server error: ${res.status}`);
      }
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`Synced ${data.new_campers_added || 0} new campers from CampMinder (${data.already_in_system || 0} already in system)`);
        fetchData();
        autoSyncToSheets();
      } else {
        toast.error('Sync failed: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        toast.error('CampMinder sync timed out - please try again');
      } else {
        toast.error('Sync error: ' + error.message);
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleFreshSync = async () => {
    if (!window.confirm('This will CLEAR ALL camper data and re-sync from CampMinder. All assignments will be lost! Continue?')) {
      return;
    }
    setSyncing(true);
    setMobileMenuOpen(false);
    try {
      // Use the new full sync endpoint
      const res = await fetch(`${API_URL}/api/sync/campminder/full`, { method: 'POST' });
      
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(text || `Server error: ${res.status}`);
      }
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`Fresh sync complete: ${data.campers_added || 0} campers loaded`);
        fetchData();
        autoSyncToSheets();
      } else {
        toast.error('Fresh sync failed: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Fresh sync error: ' + error.message);
    } finally {
      setSyncing(false);
    }
  };

  const handlePreferencesUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    setMobileMenuOpen(false);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API_URL}/api/sync/preferences`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`Updated preferences for ${data.updated} campers`);
        fetchData();
      } else {
        toast.error('Import failed: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Import error: ' + error.message);
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const handleResetGroups = async () => {
    if (!window.confirm('Reset all group definitions to fix gender/grade assignments? This will NOT change camper assignments.')) {
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/groups/reset`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Fixed ${data.groups_count} group definitions`);
        fetchData();
      } else {
        toast.error('Reset failed: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Reset error: ' + error.message);
    }
  };

  const handleCleanupCITs = async () => {
    if (!window.confirm('Remove all CIT campers (9th/10th grade) from the system?')) {
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/sync/cleanup-cits`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Removed ${data.cits_removed} CIT campers`);
        fetchData();
      } else {
        toast.error('Cleanup failed: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Cleanup error: ' + error.message);
    }
  };

  const handleCleanupHoliday = async () => {
    if (!window.confirm('Remove all holiday camp campers (Winter Break, Spring Break, etc.)? This will delete campers not enrolled in summer sessions.')) {
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/sync/cleanup-holiday`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Removed ${data.deleted} holiday/non-summer campers`);
        fetchData();
      } else {
        toast.error('Cleanup failed: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Cleanup error: ' + error.message);
    }
  };

  const handleSyncFromSheet = async () => {
    setSyncingSheet(true);
    setMobileMenuOpen(false);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minute timeout
      
      const res = await fetch(`${API_URL}/api/sync/google-sheet`, { 
        method: 'POST',
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(text || `Server error: ${res.status}`);
      }
      
      const data = await res.json();
      
      if (res.ok) {
        let msg = `Synced ${data.updated} campers`;
        if (data.groups_synced > 0) {
          msg += `, ${data.groups_synced} group changes`;
        }
        if (data.removed_count > 0) {
          msg += `, removed ${data.removed_count} unenrolled`;
        }
        toast.success(msg);
        if (data.removed_count > 0) {
          toast.info(`Removed: ${data.removed.slice(0, 5).join(', ')}${data.removed_count > 5 ? '...' : ''}`, { duration: 6000 });
        }
        if (data.not_found_count > 0) {
          toast.warning(`${data.not_found_count} campers on sheet not found in system`, { duration: 8000 });
        }
        fetchData();
        autoSyncToSheets();
      } else {
        toast.error('Sheet sync failed: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        toast.error('Sync timed out - please try again');
      } else {
        toast.error('Sheet sync error: ' + error.message);
      }
    } finally {
      setSyncingSheet(false);
    }
  };

  const handleFullSyncFromSheet = async () => {
    if (!window.confirm('This will sync ALL data from Google Sheet:\n- Update group assignments\n- Create missing campers\n- Remove unenrolled campers\n\nContinue?')) {
      return;
    }
    setSyncingSheet(true);
    setMobileMenuOpen(false);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);
      
      const res = await fetch(`${API_URL}/api/sync/google-sheet/full`, { 
        method: 'POST',
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(text || `Server error: ${res.status}`);
      }
      
      const data = await res.json();
      
      if (res.ok) {
        let msg = `Synced ${data.updated} campers`;
        if (data.created > 0) {
          msg += `, created ${data.created} new`;
        }
        if (data.groups_restored > 0) {
          msg += `, ${data.groups_restored} group changes`;
        }
        if (data.removed_count > 0) {
          msg += `, removed ${data.removed_count} unenrolled`;
        }
        toast.success(msg);
        if (data.removed_count > 0) {
          toast.info(`Removed: ${data.removed.slice(0, 5).join(', ')}${data.removed_count > 5 ? '...' : ''}`, { duration: 6000 });
        }
        fetchData();
        autoSyncToSheets();
      } else {
        toast.error('Full sync failed: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        toast.error('Sync timed out - please try again');
      } else {
        toast.error('Full sync error: ' + error.message);
      }
    } finally {
      setSyncingSheet(false);
    }
  };

  const handleSyncCampMinderPrefs = async () => {
    setSyncingSheet(true);
    setMobileMenuOpen(false);
    try {
      const res = await fetch(`${API_URL}/api/sync/campminder-preferences`, { method: 'POST' });
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`Synced preferences: ${data.with_share_preferences} share, ${data.with_avoid_preferences} avoid`);
        fetchData();
        autoSyncToSheets();
      } else {
        toast.error('CampMinder prefs sync failed: ' + (data.error || data.detail || 'Unknown error'));
      }
    } catch (error) {
      toast.error('CampMinder prefs sync error: ' + error.message);
    } finally {
      setSyncingSheet(false);
    }
  };

  const handleAutoGroup = async (redistributeAll = false) => {
    setAutoGrouping(true);
    setMobileMenuOpen(false);
    try {
      const res = await fetch(`${API_URL}/api/auto-group`, { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ redistribute_all: redistributeAll })
      });
      const data = await res.json();
      
      if (res.ok) {
        if (redistributeAll) {
          toast.success(`Redistributed ALL ${data.newly_assigned} campers evenly across groups`);
        } else {
          if (data.newly_assigned > 0) {
            toast.success(`Assigned ${data.newly_assigned} new campers (kept ${data.already_assigned} existing)`);
          } else {
            toast.info(`All campers already assigned (${data.already_assigned} total)`);
          }
        }
        if (data.unassigned_count > 0) {
          toast.warning(`${data.unassigned_count} campers could not be assigned`);
        }
        fetchData();
        // Auto-sync to Google Sheets after auto-grouping
        autoSyncToSheets();
      } else {
        toast.error('Auto-group failed: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Auto-group error: ' + error.message);
    } finally {
      setAutoGrouping(false);
    }
  };

  const handleRedistributeAll = async () => {
    const passcode = window.prompt('⚠️ This will CLEAR ALL existing group assignments!\n\nEnter passcode to confirm:');
    if (passcode !== '1993') {
      if (passcode !== null) {
        toast.error('Incorrect passcode. Redistribute cancelled.');
      }
      return;
    }
    await handleAutoGroup(true);
  };

  const handleExport = async () => {
    setMobileMenuOpen(false);
    try {
      const csvRes = await fetch(`${API_URL}/api/export/csv`);
      const csvBlob = await csvRes.blob();
      const url = window.URL.createObjectURL(csvBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'camper_groups.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Downloaded camper groups');
    } catch (error) {
      toast.error('Error downloading: ' + error.message);
    }
  };

  const [changeLogDate, setChangeLogDate] = useState(new Date().toISOString().slice(0, 10));
  const [changeLogData, setChangeLogData] = useState(null);
  const [changeLogDates, setChangeLogDates] = useState([]);
  const [showChangeLog, setShowChangeLog] = useState(false);

  const fetchChangeLog = async (date) => {
    try {
      const [listRes, datesRes] = await Promise.all([
        fetch(`${API_URL}/api/changes/list?date=${date}`),
        fetch(`${API_URL}/api/changes/dates`)
      ]);
      if (listRes.ok) setChangeLogData(await listRes.json());
      if (datesRes.ok) {
        const d = await datesRes.json();
        setChangeLogDates(d.dates || []);
      }
    } catch (e) { console.error(e); }
  };

  const handleExportChanges = async (date) => {
    const targetDate = date || changeLogDate;
    try {
      const csvRes = await fetch(`${API_URL}/api/export/changes?date=${targetDate}`);
      const csvBlob = await csvRes.blob();
      const url = window.URL.createObjectURL(csvBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `group_changes_${targetDate}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded changes for ${targetDate}`);
    } catch (error) {
      toast.error('Error downloading changes: ' + error.message);
    }
  };

  const handleExportGroupSummary = async () => {
    setMobileMenuOpen(false);
    try {
      const csvRes = await fetch(`${API_URL}/api/export/group-summary`);
      const csvBlob = await csvRes.blob();
      const url = window.URL.createObjectURL(csvBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'group_summary_2026.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Downloaded group summary report');
    } catch (error) {
      toast.error('Error downloading summary: ' + error.message);
    }
  };

  const [syncingToSheets, setSyncingToSheets] = useState(false);
  const [sheetsUrl, setSheetsUrl] = useState(null);
  const [flexPushStatus, setFlexPushStatus] = useState(null);

  // Poll Snapshot flex push freshness
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await fetch(`${API_URL}/api/external/flex-days/status`);
        if (r.ok && !cancelled) setFlexPushStatus(await r.json());
      } catch (e) { /* silent */ }
    }
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Silent auto-sync to Google Sheets
  const autoSyncToSheets = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/sheets/sync`, { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
    } catch (error) {
      console.log('Auto-sync to sheets failed:', error.message);
    }
  }, []);

  const handleSyncToGoogleSheets = async () => {
    setMobileMenuOpen(false);
    setSyncingToSheets(true);
    try {
      const res = await fetch(`${API_URL}/api/sheets/sync`, { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`Google Sheet sync triggered! ${data.groups_sent} groups being updated in background.`);
      } else {
        if (data.detail?.includes('not configured')) {
          toast.error('Google Sheets not configured. Please add credentials to backend.');
        } else {
          toast.error('Sync failed: ' + (data.detail || 'Unknown error'));
        }
      }
    } catch (error) {
      toast.error('Error syncing to Google Sheets: ' + error.message);
    } finally {
      setSyncingToSheets(false);
    }
  };

  const handleExportGroupings = async () => {
    setMobileMenuOpen(false);
    try {
      const csvRes = await fetch(`${API_URL}/api/export/groupings`);
      const csvBlob = await csvRes.blob();
      const url = window.URL.createObjectURL(csvBlob);
      const a = document.createElement('a');
      a.href = url;
      // Get filename from content-disposition header or use default
      const contentDisposition = csvRes.headers.get('content-disposition');
      const filename = contentDisposition?.match(/filename="?(.+)"?/)?.[1] || `groupings_${new Date().toISOString().slice(0,10)}.csv`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Downloaded full groupings report');
    } catch (error) {
      toast.error('Error downloading groupings: ' + error.message);
    }
  };

  const handleAssignCamper = async (camperId, groupCode) => {
    try {
      const assignGroupCode = groupCode || null;
      
      const res = await fetch(`${API_URL}/api/campers/${camperId}/assign`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ camper_id: camperId, group_code: assignGroupCode })
      });
      
      if (res.ok) {
        const data = await res.json();
        if (!groupCode) {
          toast.success('Camper unassigned');
        } else if (data.violations?.length > 0) {
          toast.warning(`Assigned with ${data.violations.length} constraint violations`);
        } else {
          toast.success(`Assigned to ${groupCode}`);
        }
        fetchData();
        // Auto-sync to Google Sheets after assignment
        autoSyncToSheets();
      }
    } catch (error) {
      toast.error('Assignment error: ' + error.message);
    }
  };

  const handleAcknowledgeConflict = async (camper1Id, camper2Id) => {
    try {
      const res = await fetch(`${API_URL}/api/conflicts/acknowledge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ camper1_id: camper1Id, camper2_id: camper2Id })
      });
      if (res.ok) {
        // Optimistically remove the dismissed pair from the list
        setConflicts(prev => prev.filter(c => !(
          (c.camper1_id === camper1Id && c.camper2_id === camper2Id) ||
          (c.camper1_id === camper2Id && c.camper2_id === camper1Id)
        )));
        toast.success('Conflict marked OK with parent');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error('Could not acknowledge: ' + (err.detail || res.statusText));
      }
    } catch (error) {
      toast.error('Acknowledge error: ' + error.message);
    }
  };


  // Drag and Drop Handlers
  const handleDragStart = (event) => {
    const { active } = event;
    const camper = active.data.current?.camper;
    if (camper) {
      setActiveDragCamper(camper);
    }
  };

  const handleDragOver = (event) => {
    const { over } = event;
    setActiveDropGroup(over?.id || null);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveDragCamper(null);
    setActiveDropGroup(null);
    
    if (over && active.data.current?.camper) {
      const camper = active.data.current.camper;
      const newGroupCode = over.id;
      
      // Only reassign if dropped on a different group
      if (newGroupCode !== camper.assigned_group) {
        const conflictPartners = getConflictPartnersInGroup(camper.id, newGroupCode);
        if (conflictPartners.length > 0) {
          // Show confirm modal — don't apply yet
          setDragConflictPrompt({ camper, targetGroup: newGroupCode, partners: conflictPartners });
        } else {
          handleAssignCamper(camper.id, newGroupCode);
        }
      }
    }
  };

  const confirmDragMove = async (acknowledgeAll) => {
    if (!dragConflictPrompt) return;
    const { camper, targetGroup, partners } = dragConflictPrompt;
    if (acknowledgeAll) {
      // Acknowledge all conflict pairs first so they don't show up after the move
      await Promise.all(partners.map(p =>
        fetch(`${API_URL}/api/conflicts/acknowledge`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ camper1_id: camper.id, camper2_id: p.id })
        }).catch(() => null)
      ));
    }
    setDragConflictPrompt(null);
    handleAssignCamper(camper.id, targetGroup);
  };

  // Same conflict guard for dropdown-based moves: shows the modal if a conflict would be created.
  const handleAssignCamperGuarded = (camperId, groupCode) => {
    if (!groupCode) {
      handleAssignCamper(camperId, groupCode);
      return;
    }
    const partners = getConflictPartnersInGroup(camperId, groupCode);
    if (partners.length === 0) {
      handleAssignCamper(camperId, groupCode);
      return;
    }
    const camper = campers.find(c => c.id === camperId);
    if (!camper) {
      handleAssignCamper(camperId, groupCode);
      return;
    }
    setDragConflictPrompt({ camper, targetGroup: groupCode, partners });
  };

  const toggleGroupExpand = (groupCode) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupCode]: !prev[groupCode]
    }));
  };

  // Helper function to get session abbreviation
  const getSessionAbbr = (session) => {
    if (!session) return '';
    const s = session.toLowerCase();
    if (s.includes('full season')) return 'FS';
    if (s.includes('half season 1')) return 'HS1';
    if (s.includes('half season 2')) return 'HS2';
    if (s.includes('6 week')) return '6W';
    if (s.includes('flex 30') || s.includes('f30')) return 'F30';
    if (s.includes('flex 25') || s.includes('f25')) return 'F25';
    if (s.includes('flex 20') || s.includes('f20')) return 'F20';
    if (s.includes('enrolled')) return 'ENR';
    return '';
  };

  // Helper function to get session badge color
  const getSessionColor = (abbr) => {
    switch(abbr) {
      case 'FS': return 'bg-green-100 text-green-700';
      case 'HS1': return 'bg-blue-100 text-blue-700';
      case 'HS2': return 'bg-purple-100 text-purple-700';
      case '6W': return 'bg-yellow-100 text-yellow-700';
      case 'F30': return 'bg-orange-100 text-orange-700';
      case 'F25': return 'bg-pink-100 text-pink-700';
      case 'F20': return 'bg-cyan-100 text-cyan-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  // Helper function to check if a group matches a camper's grade
  // Handles combined grades like "7th-8th" and "1st-2nd"
  const gradeMatchesGroup = (camperGrade, groupGrade) => {
    if (camperGrade === groupGrade) return true;
    
    // Combined grade mappings
    if (groupGrade === '7th-8th' && (camperGrade === '7th' || camperGrade === '8th')) return true;
    if (groupGrade === '1st-2nd' && (camperGrade === '1st' || camperGrade === '2nd')) return true;
    
    return false;
  };

  // Get eligible groups for a camper (for dropdown)
  const getEligibleGroups = (camper) => {
    return groups.filter(g => 
      gradeMatchesGroup(camper.grade, g.grade) && 
      (g.gender === camper.gender || g.gender === 'Mixed')
    );
  };

  // Check whether moving `camperId` into `groupCode` would create a new avoid conflict
  // (someone in that group is on this camper's do-not-share list, or vice versa).
  // Acknowledged ("OK with parent") pairs are NOT considered conflicts.
  const wouldCreateConflict = (camperId, groupCode) => {
    return getConflictPartnersInGroup(camperId, groupCode).length > 0;
  };

  // Return the list of campers in `groupCode` who would conflict with `camperId`.
  const getConflictPartnersInGroup = (camperId, groupCode) => {
    const camper = campers.find(c => c.id === camperId);
    if (!camper) return [];
    const camperFull = `${camper.first_name} ${camper.last_name}`.toLowerCase();
    const camperFirst = camper.first_name.toLowerCase();
    const camperAvoids = (camper.do_not_share_with || []).map(n => (n || '').trim().toLowerCase()).filter(Boolean);

    return campers.filter(m => {
      if (m.assigned_group !== groupCode || m.id === camperId) return false;
      const mFull = `${m.first_name} ${m.last_name}`.toLowerCase();
      const mFirst = m.first_name.toLowerCase();
      const mAvoids = (m.do_not_share_with || []).map(n => (n || '').trim().toLowerCase()).filter(Boolean);
      return camperAvoids.includes(mFull) || camperAvoids.includes(mFirst) ||
             mAvoids.includes(camperFull) || mAvoids.includes(camperFirst);
    });
  };

  // Filter campers
  const filteredCampers = campers.filter(c => {
    if (selectedGrade !== 'all' && c.grade !== selectedGrade) return false;
    if (selectedGender !== 'all' && c.gender !== selectedGender) return false;
    if (selectedGroup !== 'all' && c.assigned_group !== selectedGroup) return false;
    if (showUnassigned && c.assigned_group) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const name = `${c.first_name} ${c.last_name}`.toLowerCase();
      const groupCode = (c.assigned_group || '').toLowerCase();
      // Find group name for this camper
      const camperGroup = groups.find(g => g.code === c.assigned_group);
      const groupName = camperGroup ? camperGroup.name.toLowerCase() : '';
      // Search in name, group code, or group name
      if (!name.includes(search) && !groupCode.includes(search) && !groupName.includes(search)) return false;
    }
    return true;
  });

  // Get unique grades for filter
  const grades = [...new Set(campers.map(c => c.grade))].filter(Boolean).sort();

  // Sort groups by code (numeric sorting: B01...B16, CIT, G01...G16, N01...N16)
  const sortedGroups = [...groups].sort((a, b) => {
    // Extract letter prefix and number
    const matchA = a.code.match(/^([A-Z]+)(\d+)$/);
    const matchB = b.code.match(/^([A-Z]+)(\d+)$/);
    
    if (matchA && matchB) {
      // Compare letter prefix first
      if (matchA[1] !== matchB[1]) {
        return matchA[1].localeCompare(matchB[1]);
      }
      // Then compare numbers numerically
      return parseInt(matchA[2]) - parseInt(matchB[2]);
    }
    // Fallback to string comparison
    return a.code.localeCompare(b.code);
  });

  // Group campers by assigned group
  const campersByGroup = {};
  groups.forEach(g => {
    campersByGroup[g.code] = campers.filter(c => c.assigned_group === g.code);
  });
  campersByGroup['unassigned'] = campers.filter(c => !c.assigned_group);

  if (!authenticated) {
    return (
      <>
        <Toaster position="top-center" richColors />
        <PasscodeScreen onAuthenticated={() => setAuthenticated(true)} />
      </>
    );
  }

  return (
    <DndContext 
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-yellow-50">
      <Toaster position="top-center" richColors />
      
      {/* Drag Overlay - shows the camper being dragged */}
      <DragOverlay>
        {activeDragCamper && (
          <div className="bg-white shadow-lg rounded-lg px-3 py-2 border-2 border-blue-400">
            <span className="font-medium">{activeDragCamper.first_name} {activeDragCamper.last_name}</span>
          </div>
        )}
      </DragOverlay>
      
      {/* Header - Responsive */}
      <header className="bg-[#3358A4] text-white shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          {/* Desktop Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <img 
                src="/rrdc-logo.png" 
                alt="Rolling River Day Camp" 
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex-shrink-0 bg-white p-0.5"
              />
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold truncate">RRDC Grouping Companion</h1>
                <p className="text-blue-200 text-xs sm:text-sm hidden sm:block">Rolling River Day Camp</p>
              </div>
            </div>
            
            {/* Desktop Action Buttons - Primary Row */}
            <div className="hidden lg:flex flex-col gap-2">
              {/* Row 1: Main Workflow */}
              <div className="flex items-center justify-end gap-2">
                <Button 
                  onClick={handleSync} 
                  disabled={syncing}
                  variant="outline"
                  size="sm"
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                  data-testid="sync-campminder-btn"
                  title="Add new campers from CampMinder (keeps existing)"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? 'Syncing...' : 'Step 1: Sync Campers'}
                </Button>
                
                <Button 
                  onClick={handleFullSyncFromSheet}
                  disabled={syncingSheet}
                  variant="outline"
                  size="sm"
                  className="bg-blue-500/20 border-blue-300 text-white hover:bg-blue-500/30"
                  data-testid="backup-sheet-btn"
                  title="BACKUP: Sync campers + groups from Google Sheet (use if CampMinder fails)"
                >
                  <FileSpreadsheet className={`w-4 h-4 mr-2 ${syncingSheet ? 'animate-spin' : ''}`} />
                  {syncingSheet ? 'Syncing...' : 'Backup: Sync from Sheet'}
                </Button>
                
                <Button 
                  onClick={handleSyncFromSheet}
                  disabled={syncingSheet}
                  variant="outline"
                  size="sm"
                  className="bg-emerald-500/20 border-emerald-300 text-white hover:bg-emerald-500/30"
                  data-testid="sync-sheet-btn"
                  title="Sync Share/Avoid preferences from Google Sheet"
                >
                  <FileSpreadsheet className={`w-4 h-4 mr-2 ${syncingSheet ? 'animate-spin' : ''}`} />
                  {syncingSheet ? 'Syncing...' : 'Step 2: Sync Prefs'}
                </Button>
                
                <Button 
                  onClick={() => handleAutoGroup(false)}
                  disabled={autoGrouping}
                  size="sm"
                  className="bg-yellow-500 hover:bg-yellow-600 text-black font-medium"
                  data-testid="auto-group-btn"
                  title="Assign only unassigned campers (preserves existing assignments)"
                >
                  <Wand2 className={`w-4 h-4 mr-2 ${autoGrouping ? 'animate-pulse' : ''}`} />
                  {autoGrouping ? 'Grouping...' : 'Step 3: Assign New'}
                </Button>
              </div>
              
              {/* Row 2: Reports & Admin */}
              <div className="flex items-center justify-end gap-2">
                <Button onClick={() => { setShowChangeLog(!showChangeLog); if (!showChangeLog) fetchChangeLog(changeLogDate); }} variant="outline" size="sm" className="bg-orange-500/20 border-orange-300 text-white hover:bg-orange-500/30 text-xs">
                  <Download className="w-3 h-3 mr-1" />
                  Changes
                </Button>
                
                <Button 
                  onClick={handleExportGroupSummary} 
                  variant="outline"
                  size="sm"
                  className="bg-purple-500/20 border-purple-300 text-white hover:bg-purple-500/30 text-xs"
                  data-testid="group-summary-btn"
                  title="Download session breakdown report for all groups"
                >
                  <Download className="w-3 h-3 mr-1" />
                  Summary
                </Button>
                
                <Button 
                  onClick={handleExportGroupings} 
                  variant="outline"
                  size="sm"
                  className="bg-teal-500/20 border-teal-300 text-white hover:bg-teal-500/30 text-xs"
                  data-testid="groupings-report-btn"
                  title="Download full groupings report (CSV format)"
                >
                  <FileSpreadsheet className="w-3 h-3 mr-1" />
                  Groupings
                </Button>
                
                <div className="w-px h-5 bg-white/30 mx-1" />
                
                <Button 
                  onClick={handleRedistributeAll}
                  disabled={autoGrouping}
                  size="sm"
                  variant="outline"
                  className="bg-red-500/20 border-red-300 text-white hover:bg-red-500/30 text-xs"
                  data-testid="redistribute-all-btn"
                  title="⚠️ Clear ALL assignments and redistribute everyone"
                >
                  <RefreshCw className={`w-3 h-3 mr-1 ${autoGrouping ? 'animate-spin' : ''}`} />
                  Redistribute
                </Button>
                
                <Button 
                  onClick={handleFreshSync} 
                  disabled={syncing}
                  variant="outline"
                  size="sm"
                  className="bg-red-500/20 border-red-300 text-white hover:bg-red-500/30 text-xs"
                  data-testid="fresh-sync-btn"
                  title="Clear all data and re-sync from CampMinder"
                >
                  <RefreshCw className={`w-3 h-3 mr-1 ${syncing ? 'animate-spin' : ''}`} />
                  Fresh Sync
                </Button>
                
                <Button 
                  onClick={handleResetGroups}
                  variant="outline"
                  size="sm"
                  className="bg-yellow-500/20 border-yellow-300 text-white hover:bg-yellow-500/30 text-xs"
                  data-testid="reset-groups-btn"
                  title="Fix group definitions (gender/grade)"
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Fix Groups
                </Button>
                
                <div className="w-px h-5 bg-white/30 mx-1" />
                
                <Button 
                  onClick={openReportWindow}
                  variant="outline"
                  size="sm"
                  className="bg-emerald-500/20 border-emerald-300 text-white hover:bg-emerald-500/30 text-xs"
                  data-testid="view-report-btn"
                >
                  <BarChart3 className="w-3 h-3 mr-1" />
                  View Report
                </Button>

                {flexPushStatus && (
                  <div
                    className={`px-2 py-1 rounded text-xs font-medium border ${
                      flexPushStatus.last_push_at
                        ? "bg-pink-500/20 border-pink-300 text-white"
                        : "bg-red-500/20 border-red-300 text-white"
                    }`}
                    data-testid="flex-push-badge"
                    title={
                      flexPushStatus.last_push_at
                        ? `Snapshot last pushed ${flexPushStatus.last_push_at}. ${flexPushStatus.campers_with_flex_data}/${flexPushStatus.flex_session_camper_total} flex campers have data.`
                        : "Snapshot has not pushed flex days yet"
                    }
                  >
                    Flex: {flexPushStatus.campers_with_flex_data ?? 0}/{flexPushStatus.flex_session_camper_total ?? 0}
                  </div>
                )}

                <Button
                  onClick={() => window.open('/admin/campfront', '_blank', 'noopener,noreferrer')}
                  variant="outline"
                  size="sm"
                  className="bg-emerald-500/20 border-emerald-300 text-white hover:bg-emerald-500/30 text-xs"
                  data-testid="campfront-sync-btn"
                  title="Open Campfront sync admin in a new tab"
                >
                  Campfront Sync
                </Button>

                <div className="w-px h-5 bg-white/30 mx-1" />
                
                <Button 
                  onClick={() => setShowSeasonManager(!showSeasonManager)}
                  variant="outline"
                  size="sm"
                  className={`text-xs ${showSeasonManager ? 'bg-amber-500/40 border-amber-300' : 'bg-white/10 border-white/30'} text-white hover:bg-amber-500/30`}
                  data-testid="season-mgmt-btn"
                  title="Season Management"
                >
                  <Archive className="w-3 h-3 mr-1" />
                  Seasons
                </Button>
                
                <Button 
                  onClick={handleLock}
                  variant="outline"
                  size="sm"
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20 text-xs"
                  data-testid="lock-btn-desktop"
                  title="Lock session"
                >
                  <Lock className="w-3 h-3 mr-1" />
                  Lock
                </Button>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center gap-1">
              <button
                onClick={handleLock}
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                title="Lock Session"
                data-testid="lock-btn-mobile"
              >
                <Lock className="w-5 h-5" />
              </button>
              <button 
                className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-3 pt-3 border-t border-white/20 space-y-2">
              <Button 
                onClick={handleSync} 
                disabled={syncing}
                variant="outline"
                className="w-full bg-white/10 border-white/30 text-white hover:bg-white/20 justify-start"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Step 1: Sync Campers from CampMinder'}
              </Button>
              
              <Button 
                onClick={handleFullSyncFromSheet}
                disabled={syncingSheet}
                variant="outline"
                className="w-full bg-blue-500/20 border-blue-300 text-white hover:bg-blue-500/30 justify-start"
              >
                <FileSpreadsheet className={`w-4 h-4 mr-2 ${syncingSheet ? 'animate-spin' : ''}`} />
                {syncingSheet ? 'Syncing...' : 'BACKUP: Sync from Google Sheet'}
              </Button>
              
              <Button 
                onClick={handleSyncFromSheet}
                disabled={syncingSheet}
                variant="outline"
                className="w-full bg-green-500/20 border-green-300 text-white hover:bg-green-500/30 justify-start"
              >
                <FileSpreadsheet className={`w-4 h-4 mr-2 ${syncingSheet ? 'animate-spin' : ''}`} />
                {syncingSheet ? 'Syncing...' : 'Step 2: Sync Prefs from Sheet'}
              </Button>
              
              <Button 
                onClick={() => handleAutoGroup(false)}
                disabled={autoGrouping}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-black justify-start"
              >
                <Wand2 className={`w-4 h-4 mr-2 ${autoGrouping ? 'animate-pulse' : ''}`} />
                {autoGrouping ? 'Grouping...' : 'Step 3: Assign New Campers Only'}
              </Button>
              
              <Button 
                onClick={handleRedistributeAll}
                disabled={autoGrouping}
                variant="outline"
                className="w-full bg-red-500/20 border-red-300 text-white hover:bg-red-500/30 justify-start"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${autoGrouping ? 'animate-spin' : ''}`} />
                ⚠️ Redistribute All (Clears Assignments)
              </Button>
              
              <Button onClick={handleResetGroups} variant="outline" className="w-full bg-gray-500/20 border-gray-300 text-white hover:bg-gray-500/30 justify-start">
                <RefreshCw className="w-4 h-4 mr-2" />
                Fix Groups (Reset Definitions)
              </Button>
              
              <Button onClick={() => { setShowChangeLog(true); fetchChangeLog(changeLogDate); setMobileMenuOpen(false); }} variant="outline" className="w-full bg-orange-500/20 border-orange-300 text-white hover:bg-orange-500/30 justify-start">
                <Download className="w-4 h-4 mr-2" />
                View/Download Changes
              </Button>
              
              <Button onClick={handleExportGroupSummary} variant="outline" className="w-full bg-purple-500/20 border-purple-300 text-white hover:bg-purple-500/30 justify-start">
                <Download className="w-4 h-4 mr-2" />
                Download Group Summary
              </Button>
              
              <Button onClick={handleExportGroupings} variant="outline" className="w-full bg-green-500/20 border-green-300 text-white hover:bg-green-500/30 justify-start">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Download Groupings Report
              </Button>
              
              <Button 
                onClick={handleFreshSync} 
                disabled={syncing}
                variant="outline"
                className="w-full bg-red-500/20 border-red-300 text-white hover:bg-red-500/30 justify-start"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                Fresh Sync (Clear & Reload)
              </Button>
              
              <Button 
                onClick={() => { openReportWindow(); setMobileMenuOpen(false); }}
                variant="outline"
                className="w-full bg-emerald-500/20 border-emerald-300 text-white hover:bg-emerald-500/30 justify-start"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                View Availability Report
              </Button>

              <Button 
                onClick={() => { setShowSeasonManager(!showSeasonManager); setMobileMenuOpen(false); }}
                variant="outline"
                className="w-full bg-amber-500/20 border-amber-300 text-white hover:bg-amber-500/30 justify-start"
                data-testid="season-mgmt-btn-mobile"
              >
                <Archive className="w-4 h-4 mr-2" />
                Season Management
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Enrollment Availability Snapshot Bar */}
      <SnapshotBar
        report={availabilityReport}
        selectedDay={selectedDay}
        onDayChange={setSelectedDay}
      />

      {/* Season Manager Panel */}
      {showSeasonManager && (
        <div className="max-w-md mx-auto px-3 sm:px-4 py-4" data-testid="season-panel">
          <SeasonManager onSeasonChange={() => { fetchData(); setShowSeasonManager(false); }} />
        </div>
      )}

      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Stats Cards - Responsive Grid */}
        {stats && (
          <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-4 mb-4 sm:mb-6">
            <Card className="bg-white shadow-sm">
              <CardContent className="p-2 sm:p-4 text-center">
                <div className="text-xl sm:text-3xl font-bold text-[#3358A4]">{stats.total_campers}</div>
                <div className="text-xs sm:text-sm text-gray-500">Total</div>
              </CardContent>
            </Card>
            
            <Card className="bg-white shadow-sm">
              <CardContent className="p-2 sm:p-4 text-center">
                <div className="text-xl sm:text-3xl font-bold text-green-600">{stats.assigned}</div>
                <div className="text-xs sm:text-sm text-gray-500">Assigned</div>
              </CardContent>
            </Card>
            
            <Card className="bg-white shadow-sm">
              <CardContent className="p-2 sm:p-4 text-center">
                <div className="text-xl sm:text-3xl font-bold text-orange-500">{stats.unassigned}</div>
                <div className="text-xs sm:text-sm text-gray-500">Unassigned</div>
              </CardContent>
            </Card>
            
            <Card className="bg-white shadow-sm">
              <CardContent className="p-2 sm:p-4 text-center">
                <div className="text-xl sm:text-3xl font-bold text-blue-500">{stats.by_gender?.Male || 0}</div>
                <div className="text-xs sm:text-sm text-gray-500">Boys</div>
              </CardContent>
            </Card>
            
            <Card className="bg-white shadow-sm">
              <CardContent className="p-2 sm:p-4 text-center">
                <div className="text-xl sm:text-3xl font-bold text-pink-500">{stats.by_gender?.Female || 0}</div>
                <div className="text-xs sm:text-sm text-gray-500">Girls</div>
              </CardContent>
            </Card>
            
            <Card className="bg-white shadow-sm">
              <CardContent className="p-2 sm:p-4 text-center">
                <div className="text-xl sm:text-3xl font-bold text-purple-500">{stats.with_share_preferences || 0}</div>
                <div className="text-xs sm:text-sm text-gray-500 truncate">Prefs</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Change Log Panel */}
        {showChangeLog && (
          <Card className="mb-4 sm:mb-6 bg-white shadow-sm border-orange-200" data-testid="change-log-panel">
            <CardHeader className="p-3 sm:p-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-sm sm:text-base flex items-center gap-2">
                  <Download className="w-4 h-4 text-orange-500" />
                  Group Changes
                </CardTitle>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={changeLogDate}
                    onChange={(e) => { setChangeLogDate(e.target.value); fetchChangeLog(e.target.value); }}
                    className="text-xs border rounded px-2 py-1 bg-white"
                    data-testid="change-log-date"
                  />
                  <Button size="sm" variant="outline" className="text-xs" onClick={() => handleExportChanges(changeLogDate)} data-testid="download-changes-csv">
                    <Download className="w-3 h-3 mr-1" /> CSV
                  </Button>
                  <button onClick={() => setShowChangeLog(false)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
                </div>
              </div>
              {changeLogDates.length > 0 && (
                <div className="flex gap-1 mt-2 flex-wrap">
                  {changeLogDates.map(d => (
                    <button
                      key={d}
                      onClick={() => { setChangeLogDate(d); fetchChangeLog(d); }}
                      className={`px-2 py-0.5 text-[11px] rounded border ${d === changeLogDate ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'}`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              )}
            </CardHeader>
            <CardContent className="px-3 sm:px-6 pb-4 max-h-64 overflow-y-auto">
              {changeLogData && changeLogData.count > 0 ? (
                <table className="w-full text-xs sm:text-sm">
                  <thead>
                    <tr className="text-left text-gray-600 border-b">
                      <th className="py-1 pr-2">Time</th>
                      <th className="py-1 pr-2">Camper</th>
                      <th className="py-1 pr-2">From</th>
                      <th className="py-1 pr-2">To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changeLogData.changes.map((c, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="py-1 pr-2 text-gray-500">{(c.timestamp || '').slice(11, 19)}</td>
                        <td className="py-1 pr-2 font-medium">{c.first_name} {c.last_name}</td>
                        <td className="py-1 pr-2"><Badge variant="outline" className="text-[10px]">{c.previous_group || 'Unassigned'}</Badge></td>
                        <td className="py-1 pr-2"><Badge className="text-[10px] bg-green-100 text-green-800">{c.new_group || 'Unassigned'}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-gray-500 py-4 text-center">No changes on {changeLogDate}</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Roster Comparison Errors */}
        <Card className={`mb-4 sm:mb-6 shadow-sm ${rosterErrors && rosterErrors.total_mismatches > 0 ? 'bg-amber-50 border-amber-300' : 'bg-green-50 border-green-300'}`} data-testid="roster-errors">
          <CardHeader className="p-3 sm:p-4 cursor-pointer" onClick={() => rosterErrors?.total_mismatches > 0 && setShowRosterErrors(!showRosterErrors)}>
            <div className="flex items-center justify-between">
              <CardTitle className={`text-sm sm:text-base flex items-center gap-2 ${rosterErrors?.total_mismatches > 0 ? 'text-amber-800' : 'text-green-800'}`}>
                <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5" />
                {rosterErrors?.total_mismatches > 0
                  ? `Roster Mismatch: ${rosterErrors.total_mismatches} differences vs CamperSnapshot`
                  : rosterErrors?.status === 'ok'
                    ? 'Roster Match: All groups verified'
                    : 'Roster Comparison: Not yet run'}
                {rosterErrors?.timestamp && (
                  <span className="text-xs text-gray-500 font-normal ml-2">
                    Last: {new Date(rosterErrors.timestamp).toLocaleString('en-US', {timeZone: 'America/New_York'})}
                  </span>
                )}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="run-comparison-btn"
                  className="text-xs"
                  disabled={comparisonRunning}
                  onClick={async (e) => {
                    e.stopPropagation();
                    setComparisonRunning(true);
                    try {
                      const res = await fetch(`${API_URL}/api/roster-comparison/run`, { method: 'POST' });
                      if (res.ok) {
                        const data = await res.json();
                        if (data.total_mismatches > 0) {
                          setRosterErrors(data);
                        } else {
                          setRosterErrors({ ...data, status: 'ok' });
                        }
                        toast.success(`Comparison complete: ${data.total_mismatches} mismatches`);
                      }
                    } catch (err) {
                      toast.error('Comparison failed');
                    }
                    setComparisonRunning(false);
                  }}
                >
                  <RefreshCw className={`w-3 h-3 mr-1 ${comparisonRunning ? 'animate-spin' : ''}`} />
                  {comparisonRunning ? 'Comparing...' : 'Run Comparison'}
                </Button>
                {rosterErrors?.total_mismatches > 0 && (
                  <ChevronDown className={`w-4 h-4 text-amber-600 transition-transform ${showRosterErrors ? 'rotate-180' : ''}`} />
                )}
              </div>
            </div>
          </CardHeader>
          {showRosterErrors && rosterErrors?.mismatches?.length > 0 && (
              <CardContent className="px-3 sm:px-6 pb-4 max-h-64 overflow-y-auto">
                <table className="w-full text-xs sm:text-sm">
                  <thead>
                    <tr className="text-left text-amber-700 border-b border-amber-200">
                      <th className="py-1 pr-2">Day</th>
                      <th className="py-1 pr-2">Group</th>
                      <th className="py-1 pr-2">Snapshot</th>
                      <th className="py-1 pr-2">Ours</th>
                      <th className="py-1">Diff</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rosterErrors.mismatches.map((m, i) => (
                      <tr key={i} className="border-b border-amber-100">
                        <td className="py-1 pr-2 text-black">Day {m.day} ({m.date})</td>
                        <td className="py-1 pr-2 font-medium text-black">{m.group}</td>
                        <td className="py-1 pr-2 text-black">{m.truth}</td>
                        <td className="py-1 pr-2 text-black">{m.ours}</td>
                        <td className={`py-1 font-bold ${m.diff > 0 ? 'text-red-600' : 'text-blue-600'}`}>{m.diff > 0 ? '+' : ''}{m.diff}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            )}
          </Card>

        {/* Conflicts Panel */}
        {conflicts.length > 0 && (
          <Card className="mb-4 sm:mb-6 bg-red-50 border-red-200 shadow-sm">
            <CardHeader className="p-3 sm:p-4 cursor-pointer" onClick={() => setShowConflicts(!showConflicts)}>
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm sm:text-base flex items-center gap-2 text-red-700">
                  <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5" />
                  {conflicts.length} Avoid Conflicts
                </CardTitle>
                <ChevronDown className={`w-4 h-4 text-red-500 transition-transform ${showConflicts ? 'rotate-180' : ''}`} />
              </div>
            </CardHeader>
            {showConflicts && (
              <CardContent className="p-3 sm:p-4 pt-0">
                <p className="text-xs text-red-600 mb-3">These campers should avoid each other but are in the same group. Reassign one to resolve.</p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {conflicts.map((conflict, idx) => (
                    <div key={idx} className="bg-white p-2 sm:p-3 rounded border border-red-200">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="flex-1">
                          {/* Show who wants to avoid whom */}
                          {conflict.who_avoids === 'mutual' ? (
                            <div className="text-sm">
                              <span className="font-medium">{conflict.camper1_name}</span>
                              <span className="text-red-500 mx-2">⟷</span>
                              <span className="font-medium">{conflict.camper2_name}</span>
                              <span className="text-xs text-red-600 ml-2">(mutual)</span>
                            </div>
                          ) : (
                            <div className="text-sm">
                              <span className="font-medium">{conflict.camper1_name}</span>
                              <span className="text-red-500 mx-2">→ wants to avoid →</span>
                              <span className="font-medium">{conflict.camper2_name}</span>
                            </div>
                          )}
                          <Badge variant="outline" className="mt-1 text-xs">{conflict.group}</Badge>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <label
                            className="flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1 cursor-pointer hover:bg-green-100"
                            title="Mark this conflict as approved by the parent — it will be removed from the list"
                            data-testid={`conflict-ok-${conflict.camper1_id}-${conflict.camper2_id}`}
                          >
                            <input
                              type="checkbox"
                              className="w-3 h-3 accent-green-600"
                              onChange={(e) => e.target.checked && handleAcknowledgeConflict(conflict.camper1_id, conflict.camper2_id)}
                            />
                            OK with parent
                          </label>
                          <select
                            onChange={(e) => e.target.value && handleAssignCamperGuarded(conflict.camper1_id, e.target.value)}
                            className="text-xs border rounded px-2 py-1 bg-white"
                            defaultValue=""
                          >
                            <option value="">Move {conflict.camper1_name.split(' ')[0]}...</option>
                            {sortedGroups
                              .filter(g => g.code !== conflict.group && !wouldCreateConflict(conflict.camper1_id, g.code))
                              .map(g => (
                                <option key={g.code} value={g.code}>{g.code}</option>
                              ))}
                          </select>
                          <select
                            onChange={(e) => e.target.value && handleAssignCamperGuarded(conflict.camper2_id, e.target.value)}
                            className="text-xs border rounded px-2 py-1 bg-white"
                            defaultValue=""
                          >
                            <option value="">Move {conflict.camper2_name.split(' ')[0]}...</option>
                            {sortedGroups
                              .filter(g => g.code !== conflict.group && !wouldCreateConflict(conflict.camper2_id, g.code))
                              .map(g => (
                                <option key={g.code} value={g.code}>{g.code}</option>
                              ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Filters - Responsive with Toggle on Mobile */}
        <Card className="mb-4 sm:mb-6 bg-white shadow-sm">
          <CardContent className="p-3 sm:p-4">
            {/* Mobile Filter Toggle */}
            <div className="flex items-center justify-between mb-3 sm:mb-0">
              <button 
                className="sm:hidden flex items-center gap-2 text-sm font-medium text-gray-700"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4" />
                Filters
                <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Desktop Filter Label */}
              <div className="hidden sm:flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium">Filters:</span>
              </div>
              
              <span className={`text-xs sm:text-sm ${searchTerm ? 'text-blue-600 font-semibold bg-blue-50 px-2 py-1 rounded' : 'text-gray-500'}`}>
                {filteredCampers.length} of {campers.length}
                {searchTerm && <span className="ml-1">↓</span>}
              </span>
            </div>
            
            {/* Filter Controls - Responsive */}
            <div className={`${showFilters ? 'block' : 'hidden'} sm:block`}>
              <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 sm:gap-3 mt-3 sm:mt-2">
                <Input
                  placeholder="Search name or group..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-48 lg:w-56 text-sm"
                  data-testid="search-input"
                />
                
                <div className="grid grid-cols-2 sm:flex gap-2">
                  <select
                    value={selectedGrade}
                    onChange={(e) => setSelectedGrade(e.target.value)}
                    className="border rounded-md px-2 sm:px-3 py-2 text-sm bg-white"
                    data-testid="grade-filter"
                  >
                    <option value="all">All Grades</option>
                    {grades.map(g => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                  
                  <select
                    value={selectedGender}
                    onChange={(e) => setSelectedGender(e.target.value)}
                    className="border rounded-md px-2 sm:px-3 py-2 text-sm bg-white"
                    data-testid="gender-filter"
                  >
                    <option value="all">All Genders</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Mixed">Mixed</option>
                  </select>
                </div>
                
                <div className="grid grid-cols-2 sm:flex gap-2">
                  <select
                    value={selectedGroup}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                    className="border rounded-md px-2 sm:px-3 py-2 text-sm bg-white"
                    data-testid="group-filter"
                  >
                    <option value="all">All Groups</option>
                    {groups.map(g => (
                      <option key={g.code} value={g.code}>{g.code} - {g.name}</option>
                    ))}
                  </select>
                  
                  <Button
                    variant={showUnassigned ? "default" : "outline"}
                    size="sm"
                    onClick={() => setShowUnassigned(!showUnassigned)}
                    className={`text-xs sm:text-sm ${showUnassigned ? "bg-orange-500 hover:bg-orange-600" : ""}`}
                    data-testid="unassigned-filter-btn"
                  >
                    <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    <span className="hidden sm:inline">Unassigned Only</span>
                    <span className="sm:hidden">Unassigned</span>
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* PROMINENT: Unassigned/New Campers Section - Always visible when there are unassigned */}
        {/* Updated: Gender badges with BOY/GIRL labels - v2 */}
        {campersByGroup['unassigned']?.length > 0 && !searchTerm && (
          <Card className="mb-4 sm:mb-6 bg-orange-50 border-2 border-orange-400 shadow-lg" data-testid="needs-assignment-card">
            <CardHeader className="pb-2 px-3 sm:px-6 bg-orange-100">
              <CardTitle className="flex items-center justify-between text-sm sm:text-lg">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                  <span className="text-orange-800 font-bold">⚠️ NEEDS MANUAL ASSIGNMENT</span>
                </div>
                <Badge className="bg-orange-600 text-white text-sm px-3 py-1">
                  {campersByGroup['unassigned'].length} campers
                </Badge>
              </CardTitle>
              <p className="text-xs sm:text-sm text-orange-700 mt-1">
                These campers need to be assigned to groups. Use the dropdown to assign each one.
              </p>
            </CardHeader>
            <CardContent className="max-h-[400px] overflow-y-auto px-3 sm:px-6 py-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {campersByGroup['unassigned'].map(camper => {
                  const sessionAbbr = getSessionAbbr(camper.session);
                  const genderColor = camper.gender === 'Male' ? 'bg-blue-100 text-blue-700 border-blue-300' : 
                                      camper.gender === 'Female' ? 'bg-pink-100 text-pink-700 border-pink-300' : 
                                      'bg-gray-100 text-gray-700 border-gray-300';
                  const cardBorder = camper.gender === 'Male' ? 'border-blue-400' : 
                                     camper.gender === 'Female' ? 'border-pink-400' : 'border-orange-300';
                  return (
                    <div 
                      key={camper.id}
                      className={`flex flex-col p-3 bg-white rounded-lg border-2 ${cardBorder} shadow-sm`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded border ${genderColor}`}>
                            {camper.gender === 'Male' ? '♂ BOY' : camper.gender === 'Female' ? '♀ GIRL' : '? N/A'}
                          </span>
                          {sessionAbbr && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${getSessionColor(sessionAbbr)}`}>
                              {sessionAbbr}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="font-semibold text-sm mb-1">{camper.first_name} {camper.last_name}</div>
                      <div className="text-xs text-gray-600 mb-2">
                        <span className="font-medium">{camper.grade}</span>
                        {camper.city && <span className="text-gray-400"> • {camper.city}</span>}
                        <span className="text-purple-600 ml-1">PG:{camper.previous_group || '-'}</span>
                        {(camperHistory[camper.id] || []).map(h => (
                          <span key={h.year} className="text-amber-700 ml-1" title={`${h.year}: ${h.group_name}`}>
                            {h.year.toString().slice(-2)}:{h.group_code}
                          </span>
                        ))}
                      </div>
                      {camper.share_with?.length > 0 && (
                        <div className="text-xs text-green-600 mb-1">
                          <Check className="w-3 h-3 inline mr-1" />
                          Wants: {camper.share_with.slice(0, 2).join(', ')}{camper.share_with.length > 2 && '...'}
                        </div>
                      )}
                      {camper.do_not_share_with?.length > 0 && (
                        <div className="text-xs text-red-600 mb-1">
                          <X className="w-3 h-3 inline mr-1" />
                          Avoid: {camper.do_not_share_with.slice(0, 2).join(', ')}{camper.do_not_share_with.length > 2 && '...'}
                        </div>
                      )}
                      <select
                        onChange={(e) => e.target.value && handleAssignCamperGuarded(camper.id, e.target.value)}
                        className="text-sm border-2 border-orange-300 rounded-md px-2 py-2 bg-white w-full mt-auto font-medium"
                        defaultValue=""
                      >
                        <option value="">Select group...</option>
                        {sortedGroups.map(g => {
                          const genderLabel = g.gender === 'Male' ? 'Boys' : g.gender === 'Female' ? 'Girls' : 'Mixed';
                          return (
                            <option key={g.code} value={g.code}>
                              {g.code} - {g.name} ({genderLabel}) [{g.current_count}/{g.max_size}]
                            </option>
                          );
                        })}
                        <option disabled>── All Groups ──</option>
                        {sortedGroups.map(g => {
                          const genderLabel = g.gender === 'Male' ? 'Boys' : g.gender === 'Female' ? 'Girls' : 'Mixed';
                          return (
                            <option key={`all-${g.code}`} value={g.code}>
                              {g.code} - {g.name} ({g.grade} {genderLabel}) [{g.current_count}/{g.max_size}]
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Camper Search Results - Show at TOP when searching */}
        {searchTerm && (
          <Card ref={filteredResultsRef} className="bg-white shadow-sm border-blue-200 border-2">
            <CardHeader className="px-3 sm:px-6 bg-blue-50">
              <CardTitle className="flex items-center gap-2 text-sm sm:text-base text-blue-800">
                <Users className="w-4 h-4 sm:w-5 sm:h-5" />
                Search Results: &quot;{searchTerm}&quot; ({filteredCampers.length} found)
              </CardTitle>
            </CardHeader>
            <CardContent className="px-0 sm:px-6">
              {filteredCampers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No campers found matching &quot;{searchTerm}&quot;
                </div>
              ) : (
                <>
                  {/* Mobile Card View */}
                  <div className="sm:hidden max-h-80 overflow-y-auto px-3">
                    {filteredCampers.slice(0, 20).map(camper => (
                      <div key={camper.id} className="border-b py-3 last:border-0">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <span className="font-medium text-sm">{camper.first_name} {camper.last_name}</span>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {camper.grade} • {camper.gender}
                            </div>
                          </div>
                          {camper.assigned_group ? (
                            <Badge variant="outline" className="text-xs">{camper.assigned_group}</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-orange-100 text-orange-700 text-xs">Unassigned</Badge>
                          )}
                        </div>
                        <select
                          onChange={(e) => handleAssignCamperGuarded(camper.id, e.target.value)}
                          className="text-xs border rounded px-2 py-1.5 w-full bg-white"
                          value={camper.assigned_group || ''}
                        >
                          <option value="">Unassign</option>
                          {sortedGroups.map(g => {
                            const genderLabel = g.gender === 'Male' ? 'Boys' : g.gender === 'Female' ? 'Girls' : 'Mixed';
                            return (
                              <option key={g.code} value={g.code}>{g.code} - {g.name} ({genderLabel})</option>
                            );
                          })}
                        </select>
                      </div>
                    ))}
                  </div>
                  
                  {/* Desktop Table View */}
                  <div className="hidden sm:block max-h-80 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left p-2">Name</th>
                          <th className="text-left p-2">Grade</th>
                          <th className="text-left p-2">Gender</th>
                          <th className="text-left p-2">Group</th>
                          <th className="text-left p-2">Wants/Avoids</th>
                          <th className="text-left p-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCampers.slice(0, 50).map(camper => {
                          const sessionAbbr = getSessionAbbr(camper.session);
                          return (
                          <tr key={camper.id} className="border-b hover:bg-gray-50">
                            <td className="p-2 font-medium">
                              <div className="flex items-center gap-2">
                                <span>{camper.first_name} {camper.last_name}</span>
                                {sessionAbbr && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${getSessionColor(sessionAbbr)}`}>
                                    {sessionAbbr}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="p-2">{camper.grade}</td>
                            <td className="p-2">{camper.gender}</td>
                            <td className="p-2">
                              {camper.assigned_group ? (
                                <Badge variant="outline">{camper.assigned_group}</Badge>
                              ) : (
                                <Badge variant="outline" className="bg-orange-100 text-orange-700">Unassigned</Badge>
                              )}
                            </td>
                            <td className="p-2">
                              {camper.share_with?.length > 0 && (
                                <div className="text-xs text-green-600">
                                  <Check className="w-3 h-3 inline mr-1" />
                                  {camper.share_with.join(', ')}
                                </div>
                              )}
                              {camper.do_not_share_with?.length > 0 && (
                                <div className="text-xs text-red-600">
                                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                                  {camper.do_not_share_with.join(', ')}
                                </div>
                              )}
                              {!camper.share_with?.length && !camper.do_not_share_with?.length && (
                                <span className="text-xs text-gray-400">-</span>
                              )}
                            </td>
                            <td className="p-2">
                              <select
                                onChange={(e) => handleAssignCamperGuarded(camper.id, e.target.value)}
                                className="text-xs border rounded px-2 py-1"
                                value={camper.assigned_group || ''}
                              >
                                <option value="">Unassign</option>
                                {sortedGroups.map(g => {
                                  const genderLabel = g.gender === 'Male' ? 'Boys' : g.gender === 'Female' ? 'Girls' : 'Mixed';
                                  return (
                                    <option key={g.code} value={g.code}>{g.code} - {g.name} ({genderLabel})</option>
                                  );
                                })}
                              </select>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  {filteredCampers.length > 50 && (
                    <div className="text-center text-gray-500 py-2 text-xs">
                      Showing first 50 of {filteredCampers.length} matches
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Groups Grid - Responsive */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {/* Group cards */}
          {sortedGroups.map(group => {
            const groupCampers = (campersByGroup[group.code] || []).slice().sort((a, b) => {
              // Sort alphabetically by last name, then first name
              const lastNameCompare = (a.last_name || '').localeCompare(b.last_name || '');
              if (lastNameCompare !== 0) return lastNameCompare;
              return (a.first_name || '').localeCompare(b.first_name || '');
            });
            const isExpanded = expandedGroups[group.code];
            const utilization = group.max_size > 0 ? Math.round((groupCampers.length / group.max_size) * 100) : 0;
            
            let bgColor = 'bg-white';
            let borderColor = 'border-gray-200';
            
            if (group.gender === 'Male') {
              bgColor = 'bg-blue-50';
              borderColor = 'border-blue-200';
            } else if (group.gender === 'Female') {
              bgColor = 'bg-pink-50';
              borderColor = 'border-pink-200';
            } else if (group.gender === 'Mixed') {
              bgColor = 'bg-purple-50';
              borderColor = 'border-purple-200';
            }
            
            const isDropTarget = activeDropGroup === group.code;
            
            return (
              <DroppableGroup key={group.code} groupCode={group.code} isOver={isDropTarget}>
              <Card 
                className={`${bgColor} ${borderColor} shadow-sm transition-all ${isDropTarget ? 'ring-2 ring-blue-400 scale-[1.02]' : ''}`}
                data-testid={`group-card-${group.code}`}
              >
                <CardHeader 
                  className="pb-2 cursor-pointer px-3 sm:px-6 py-3 sm:py-4 active:bg-gray-100/50 transition-colors" 
                  onClick={() => toggleGroupExpand(group.code)}
                >
                  <CardTitle className="flex items-center justify-between text-sm sm:text-base">
                    <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                      <span className="font-bold">{group.code}</span>
                      <span className="text-gray-600 truncate">{group.name}</span>
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                      <Badge variant="outline" className={`text-xs
                        ${utilization >= 90 ? 'bg-red-100 text-red-700' : 
                          utilization >= 70 ? 'bg-yellow-100 text-yellow-700' : 
                          'bg-green-100 text-green-700'}
                      `}>
                        {groupCampers.length}/{group.max_size}
                      </Badge>
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </CardTitle>
                  <div className="flex items-center gap-1.5 sm:gap-2 text-xs text-gray-500 mt-1">
                    <span>{group.grade}</span>
                    <span>•</span>
                    <span>{group.gender}</span>
                    <span>•</span>
                    <span>{utilization}% full</span>
                  </div>
                  {/* Session breakdown */}
                  {group.session_breakdown && Object.keys(group.session_breakdown).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {Object.entries(group.session_breakdown)
                        .filter(([_, count]) => count > 0)
                        .sort((a, b) => b[1] - a[1])
                        .map(([session, count]) => (
                          <span 
                            key={session}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 font-medium"
                            title={`${session}: ${count} campers`}
                          >
                            {session}:{count}
                          </span>
                        ))}
                    </div>
                  )}
                  {/* Availability */}
                  {group.availability && (
                    <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-gray-200">
                      <span className="text-[10px] font-semibold text-gray-500">AVAIL:</span>
                      <span 
                        className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                          group.availability.full_season > 0 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-red-100 text-red-700'
                        }`}
                        title="Full Season availability (includes 6W, F25, F30)"
                      >
                        FS:{group.availability.full_season}
                      </span>
                      <span 
                        className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                          group.availability.half_season_1 > 0 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-red-100 text-red-700'
                        }`}
                        title="Half Season 1 availability"
                      >
                        HS1:{group.availability.half_season_1}
                      </span>
                      <span 
                        className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                          group.availability.half_season_2 > 0 
                            ? 'bg-purple-100 text-purple-700' 
                            : 'bg-red-100 text-red-700'
                        }`}
                        title="Half Season 2 availability"
                      >
                        HS2:{group.availability.half_season_2}
                      </span>
                    </div>
                  )}
                  {/* Day-specific availability from report */}
                  <GroupAvailabilityBadge report={availabilityReport} groupCode={group.code} selectedDay={selectedDay} />
                  {/* FLEX Availability PDF button */}
                  <button
                    onClick={(e) => { e.stopPropagation(); window.open(`${API_URL}/api/availability/flex-pdf/${group.code}`, '_blank'); }}
                    className="ml-2 px-1.5 py-0.5 text-[10px] bg-blue-100 text-blue-700 rounded hover:bg-blue-200 border border-blue-300"
                    title="View FLEX availability dates (admin)"
                    data-testid={`flex-pdf-${group.code}`}
                  >
                    FLEX
                  </button>
                  {/* FLEX Parent PDF button */}
                  <button
                    onClick={(e) => { e.stopPropagation(); window.open(`${API_URL}/api/availability/flex-parent-pdf/${group.code}`, '_blank'); }}
                    className="ml-2 px-1.5 py-0.5 text-[10px] bg-blue-100 text-blue-700 rounded hover:bg-blue-200 border border-blue-300"
                    title="Parent-facing PDF: day & date only"
                    data-testid={`flex-parent-pdf-${group.code}`}
                  >
                    FLEX Parent
                  </button>
                  {/* Per-group Avoid Conflicts dropdown */}
                  {(() => {
                    const groupConflicts = conflicts.filter(c => c.group === group.code);
                    if (groupConflicts.length === 0) return null;
                    return (
                      <details
                        className="ml-2 inline-block"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`group-conflicts-${group.code}`}
                      >
                        <summary
                          className="cursor-pointer list-none px-1.5 py-0.5 text-[10px] bg-red-100 text-red-700 rounded hover:bg-red-200 border border-red-300 inline-flex items-center gap-1"
                          title="Avoid conflicts in this group"
                        >
                          <AlertTriangle className="w-3 h-3" />
                          {groupConflicts.length} Avoid
                        </summary>
                        <div className="absolute z-20 mt-1 w-80 max-h-72 overflow-y-auto bg-white border border-red-200 rounded shadow-lg p-2 text-xs">
                          {groupConflicts.map((conflict, idx) => (
                            <div key={idx} className="py-1.5 border-b border-red-100 last:border-b-0">
                              <div className="text-gray-800">
                                {conflict.who_avoids === 'mutual' ? (
                                  <span><span className="font-medium">{conflict.camper1_name}</span> <span className="text-red-500">⟷</span> <span className="font-medium">{conflict.camper2_name}</span></span>
                                ) : (
                                  <span><span className="font-medium">{conflict.camper1_name}</span> <span className="text-red-500">→ avoids →</span> <span className="font-medium">{conflict.camper2_name}</span></span>
                                )}
                              </div>
                              <div className="mt-1 flex flex-wrap items-center gap-1">
                                <label
                                  className="inline-flex items-center gap-1 text-[10px] text-green-700 bg-green-50 border border-green-200 rounded px-1.5 py-0.5 cursor-pointer hover:bg-green-100"
                                  title="Mark this conflict as approved by the parent"
                                  data-testid={`group-conflict-ok-${conflict.camper1_id}-${conflict.camper2_id}`}
                                >
                                  <input
                                    type="checkbox"
                                    className="w-3 h-3 accent-green-600"
                                    onChange={(e) => e.target.checked && handleAcknowledgeConflict(conflict.camper1_id, conflict.camper2_id)}
                                  />
                                  OK with parent
                                </label>
                                <select
                                  onChange={(e) => e.target.value && handleAssignCamperGuarded(conflict.camper1_id, e.target.value)}
                                  className="text-[10px] border rounded px-1 py-0.5 bg-white"
                                  defaultValue=""
                                  data-testid={`group-conflict-move1-${conflict.camper1_id}`}
                                >
                                  <option value="">Move {conflict.camper1_name.split(' ')[0]}...</option>
                                  {sortedGroups
                                    .filter(g => g.code !== conflict.group && !wouldCreateConflict(conflict.camper1_id, g.code))
                                    .map(g => (
                                      <option key={g.code} value={g.code}>{g.code}</option>
                                    ))}
                                </select>
                                <select
                                  onChange={(e) => e.target.value && handleAssignCamperGuarded(conflict.camper2_id, e.target.value)}
                                  className="text-[10px] border rounded px-1 py-0.5 bg-white"
                                  defaultValue=""
                                  data-testid={`group-conflict-move2-${conflict.camper2_id}`}
                                >
                                  <option value="">Move {conflict.camper2_name.split(' ')[0]}...</option>
                                  {sortedGroups
                                    .filter(g => g.code !== conflict.group && !wouldCreateConflict(conflict.camper2_id, g.code))
                                    .map(g => (
                                      <option key={g.code} value={g.code}>{g.code}</option>
                                    ))}
                                </select>
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    );
                  })()}
                </CardHeader>
                
                {isExpanded && (
                  <CardContent className="max-h-[70vh] overflow-y-auto px-3 sm:px-6">
                    {groupCampers.length === 0 ? (
                      <div className="text-center text-gray-400 py-4 text-sm">No campers assigned - drag campers here!</div>
                    ) : (
                      groupCampers.map(camper => {
                        const sessionAbbr = getSessionAbbr(camper.session);
                        const genderBadge = camper.gender === 'Male' ? 'bg-blue-100 text-blue-700' : 
                                           camper.gender === 'Female' ? 'bg-pink-100 text-pink-700' : 
                                           'bg-gray-100 text-gray-600';
                        const genderIcon = camper.gender === 'Male' ? '♂' : camper.gender === 'Female' ? '♀' : '?';
                        return (
                        <DraggableCamper key={camper.id} camper={camper}>
                        <div 
                          className="flex flex-col sm:flex-row sm:items-center justify-between py-2 border-b border-gray-100 last:border-0 hover:bg-gray-50/50 rounded px-1 gap-1 sm:gap-2 flex-1"
                          data-testid={`camper-row-${camper.id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${genderBadge}`}>
                                {genderIcon}
                              </span>
                              <span className="font-medium text-sm">{camper.first_name} {camper.last_name}</span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-medium">
                                {camper.grade}
                              </span>
                              {sessionAbbr && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${getSessionColor(sessionAbbr)}`}>
                                  {sessionAbbr}
                                </span>
                              )}
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-medium" title="Previous Group">
                                PG:{camper.previous_group || '-'}
                              </span>
                              {(camperHistory[camper.id] || []).map(h => (
                                <span key={h.year} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium" title={`${h.year}: ${h.group_name} (${h.division})`}>
                                  {h.year.toString().slice(-2)}:{h.group_code}
                                </span>
                              ))}
                            </div>
                            {camper.share_with?.length > 0 && (
                              <div className="text-xs text-green-600">
                                <Check className="w-3 h-3 inline mr-1" />
                                Wants: {camper.share_with.join(', ')}
                              </div>
                            )}
                            {camper.do_not_share_with?.length > 0 && (
                              <div className="text-xs text-red-600">
                                <AlertTriangle className="w-3 h-3 inline mr-1" />
                                Avoid: {camper.do_not_share_with.join(', ')}
                              </div>
                            )}
                          </div>
                          <select
                            value={camper.assigned_group || ''}
                            onChange={(e) => handleAssignCamperGuarded(camper.id, e.target.value)}
                            className="text-xs border rounded px-2 py-1.5 bg-white cursor-pointer hover:border-blue-400 focus:ring-1 focus:ring-blue-400 w-full sm:w-auto mt-1 sm:mt-0"
                            data-testid={`camper-reassign-${camper.id}`}
                          >
                            <option value="">Unassign</option>
                            {sortedGroups.map(g => {
                              const genderLabel = g.gender === 'Male' ? 'Boys' : g.gender === 'Female' ? 'Girls' : 'Mixed';
                              return (
                                <option key={g.code} value={g.code}>{g.code} - {g.name} ({genderLabel})</option>
                              );
                            })}
                          </select>
                        </div>
                        </DraggableCamper>
                      );
                      })
                    )}
                  </CardContent>
                )}
              </Card>
              </DroppableGroup>
            );
          })}
        </div>

        {/* Camper List (filtered by other filters, NOT search) - Responsive Table */}
        {!searchTerm && (selectedGrade !== 'all' || selectedGender !== 'all' || showUnassigned) && (
          <Card className="mt-4 sm:mt-6 bg-white shadow-sm">
            <CardHeader className="px-3 sm:px-6">
              <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                <Users className="w-4 h-4 sm:w-5 sm:h-5" />
                Filtered Campers ({filteredCampers.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="px-0 sm:px-6">
              {/* Mobile Card View */}
              <div className="sm:hidden max-h-96 overflow-y-auto px-3">
                {filteredCampers.slice(0, 50).map(camper => (
                  <div key={camper.id} className="border-b py-3 last:border-0">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="font-medium text-sm">{camper.first_name} {camper.last_name}</span>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {camper.grade} • {camper.gender} • {camper.city}
                        </div>
                      </div>
                      {camper.assigned_group ? (
                        <Badge variant="outline" className="text-xs">{camper.assigned_group}</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-orange-100 text-orange-700 text-xs">Unassigned</Badge>
                      )}
                    </div>
                    <select
                      onChange={(e) => handleAssignCamperGuarded(camper.id, e.target.value)}
                      className="text-xs border rounded px-2 py-1.5 w-full bg-white"
                      value={camper.assigned_group || ''}
                    >
                      <option value="">Unassign</option>
                      {sortedGroups.map(g => {
                          const genderLabel = g.gender === 'Male' ? 'Boys' : g.gender === 'Female' ? 'Girls' : 'Mixed';
                          return (
                            <option key={g.code} value={g.code}>{g.code} - {g.name} ({genderLabel})</option>
                          );
                        })
                      }
                    </select>
                  </div>
                ))}
              </div>
              
              {/* Desktop Table View */}
              <div className="hidden sm:block max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Name</th>
                      <th className="text-left p-2">Grade</th>
                      <th className="text-left p-2 hidden md:table-cell">Gender</th>
                      <th className="text-left p-2 hidden lg:table-cell">City</th>
                      <th className="text-left p-2">Group</th>
                      <th className="text-left p-2">Wants/Avoids</th>
                      <th className="text-left p-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCampers.slice(0, 100).map(camper => (
                      <tr key={camper.id} className="border-b hover:bg-gray-50">
                        <td className="p-2 font-medium">{camper.first_name} {camper.last_name}</td>
                        <td className="p-2">{camper.grade}</td>
                        <td className="p-2 hidden md:table-cell">{camper.gender}</td>
                        <td className="p-2 hidden lg:table-cell">{camper.city}</td>
                        <td className="p-2">
                          {camper.assigned_group ? (
                            <Badge variant="outline">{camper.assigned_group}</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-orange-100 text-orange-700">Unassigned</Badge>
                          )}
                        </td>
                        <td className="p-2">
                          {camper.share_with?.length > 0 && (
                            <span 
                              className="text-xs text-green-600 block cursor-help" 
                              title={`Wants: ${camper.share_with.join(', ')}`}
                            >
                              <Check className="w-3 h-3 inline" /> {camper.share_with.join(', ')}
                            </span>
                          )}
                          {camper.do_not_share_with?.length > 0 && (
                            <span 
                              className="text-xs text-red-600 block cursor-help" 
                              title={`Avoid: ${camper.do_not_share_with.join(', ')}`}
                            >
                              <AlertTriangle className="w-3 h-3 inline" /> {camper.do_not_share_with.join(', ')}
                            </span>
                          )}
                          {!camper.share_with?.length && !camper.do_not_share_with?.length && (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>
                        <td className="p-2">
                          <select
                            onChange={(e) => handleAssignCamperGuarded(camper.id, e.target.value)}
                            className="text-xs border rounded px-2 py-1"
                            value={camper.assigned_group || ''}
                          >
                            <option value="">Unassign</option>
                            {sortedGroups.map(g => {
                                const genderLabel = g.gender === 'Male' ? 'Boys' : g.gender === 'Female' ? 'Girls' : 'Mixed';
                                return (
                                  <option key={g.code} value={g.code}>{g.code} - {g.name} ({genderLabel})</option>
                                );
                              })
                            }
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {filteredCampers.length > 100 && (
                <div className="text-center text-gray-500 py-2 text-xs sm:text-sm">
                  Showing first 100 of {filteredCampers.length} campers
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* Drag conflict confirmation modal */}
      {dragConflictPrompt && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setDragConflictPrompt(null)}
          data-testid="drag-conflict-modal"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-semibold text-gray-900">Avoid conflict in {dragConflictPrompt.targetGroup}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Moving <span className="font-medium">{dragConflictPrompt.camper.first_name} {dragConflictPrompt.camper.last_name}</span> to <span className="font-medium">{dragConflictPrompt.targetGroup}</span> conflicts with:
                </p>
                <ul className="text-sm text-red-700 mt-2 list-disc pl-5 space-y-0.5">
                  {dragConflictPrompt.partners.map(p => (
                    <li key={p.id}>{p.first_name} {p.last_name}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 mt-4">
              <button
                onClick={() => setDragConflictPrompt(null)}
                className="flex-1 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border border-gray-300"
                data-testid="drag-conflict-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmDragMove(true)}
                className="flex-1 px-3 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded"
                data-testid="drag-conflict-ok-with-parent"
              >
                Move + Mark OK with parent
              </button>
              <button
                onClick={() => confirmDragMove(false)}
                className="flex-1 px-3 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded"
                data-testid="drag-conflict-move-anyway"
              >
                Move anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </DndContext>
  );
}

function AppRouter() {
  const params = new URLSearchParams(window.location.search);
  const pathname = window.location.pathname || "";
  const normalizedPath = pathname.replace(/\/$/, "").toLowerCase();
  if (normalizedPath === "/openflexdays") {
    return <OpenFlexDays />;
  }
  if (normalizedPath === "/admin/campfront") {
    // Admin-only: gate with passcode like the main app
    if (!isAuthenticated()) {
      return <PasscodeScreen onSuccess={() => window.location.reload()} />;
    }
    return <CampfrontAdmin />;
  }
  if (params.get('view') === 'report') {
    return <ReportPage />;
  }
  return <App />;
}

export default AppRouter;
// Build 1769950236
